import type { ReactNode } from "react";
import { Box, Chip, Fade, Grid, Popper, Typography } from "@mui/material";
import type { GridProps } from "@mui/material";
import type { ContactMetier, Poste, PosteSansAgent } from "models/ContactsMetiers";
import { grey } from "@mui/material/colors";
import { graphService } from "services";
import useSWR from "swr";
import { createSwrConfiguration, metiersUtils } from "utils";
import { ContactMetierCard } from "./ContactMetierCard";
import { PresenceIcon } from "./PresenceIcon";
import { popperModifiers, usePopper } from "hooks";

const inlineIconStyle = { display: "inline-block", mb: "-4px" };

function VacantChip(): ReactNode {
  return <Chip label="Vacant" variant="filled" color="warning" size="small" />;
}

interface ContactMetierPosteFragmentProps {
  poste: Poste | PosteSansAgent;
  isInline: boolean;
}
function ContactMetierPosteFragment({
  poste,
  isInline,
}: Readonly<ContactMetierPosteFragmentProps>): ReactNode {
  const { anchorEl, hoverTrigger, blurTrigger, hoverPopover, blurPopover, isPopoverOpen } =
    usePopper();

  const { data: presence, isLoading: isPresenceLoading } = useSWR(
    metiersUtils.isPosteAgent(poste) ? `/presence/${poste.agent.id}` : null,
    async () =>
      metiersUtils.isPosteAgent(poste)
        ? await graphService.getUserPresence(poste.agent.id)
        : undefined,
    createSwrConfiguration()
  );

  return (
    <Box sx={{ display: isInline ? "inline-block" : "block", pr: 1, ml: isInline ? 0 : 2 }}>
      {metiersUtils.isPosteAgent(poste) ? (
        <>
          <Popper
            open={isPopoverOpen}
            anchorEl={anchorEl}
            sx={{ zIndex: 1200 }}
            placement="top"
            keepMounted
            modifiers={popperModifiers}
            onMouseEnter={hoverPopover}
            onMouseLeave={blurPopover}
            transition>
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={{ enter: 300, exit: 100 }} easing="ease-out">
                <Box>
                  <ContactMetierCard
                    contactMetierAgent={poste.agent}
                    presence={presence}
                    isPresenceLoading={isPresenceLoading}
                    onClose={() => {
                      blurTrigger();
                      blurPopover();
                    }}
                  />
                </Box>
              </Fade>
            )}
          </Popper>
          <Typography
            component="span"
            sx={{ color: "info.main", fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap" }}
            onMouseEnter={hoverTrigger}
            onMouseLeave={blurTrigger}>
            {poste.isVacant && (
              <Typography component="span" sx={{ color: grey[500] }}>
                <VacantChip /> anciennement{" "}
              </Typography>
            )}
            <PresenceIcon
              sx={inlineIconStyle}
              fontSize={18}
              presence={presence}
              isLoading={isPresenceLoading}
            />{" "}
            {poste.agent.nom} {poste.agent.prenom}
          </Typography>
        </>
      ) : (
        <Typography component="span" sx={{ color: "info.main" }}>
          {poste.libelle}
        </Typography>
      )}
    </Box>
  );
}

interface ContactMetierLineProps extends GridProps {
  contactMetier: ContactMetier;
}
export function ContactMetierLine({
  contactMetier: { metier, postes },
  ...gridProps
}: Readonly<ContactMetierLineProps>): ReactNode {
  return (
    <Grid container size={12} {...gridProps}>
      <Typography>
        {metier.libelle}&nbsp;:{" "}
        {postes?.map((p, _i, { length }) => (
          <ContactMetierPosteFragment key={p.codeAstre} poste={p} isInline={length === 1} />
        ))}
      </Typography>
    </Grid>
  );
}
